import type { LoadedBlockSuiteDoc } from '@toeverything/infra';

type BlocksMap = {
  entries: () => IterableIterator<[string, unknown]>;
};

type YBlockUpdateSlot = {
  emit: (event: { id: string; type: 'add' | 'delete' }) => void;
};

function getBlocksMap(doc: LoadedBlockSuiteDoc): BlocksMap {
  return doc.spaceDoc.getMap('blocks') as unknown as BlocksMap;
}

function getYBlockUpdateSlot(doc: LoadedBlockSuiteDoc): YBlockUpdateSlot {
  return doc.blockCollection.slots.yBlockUpdated;
}

function hasLoadedBlocks(doc: LoadedBlockSuiteDoc): boolean {
  return Array.from(getBlocksMap(doc).entries()).length > 0;
}

export function materializeLoadedDocRoot(
  doc: LoadedBlockSuiteDoc,
  options: { force?: boolean } = {}
): boolean {
  if (!options.force && doc.root) return true;

  const blockIds = Array.from(getBlocksMap(doc).entries()).map(([id]) => id);
  if (blockIds.length === 0) return false;

  const yBlockUpdated = getYBlockUpdateSlot(doc);
  for (const id of blockIds) {
    yBlockUpdated.emit({ id, type: 'delete' });
  }
  for (const id of blockIds) {
    yBlockUpdated.emit({ id, type: 'add' });
  }

  return !!doc.root;
}

async function waitForRootAddedOrTimeout(
  doc: LoadedBlockSuiteDoc,
  timeoutMs: number
): Promise<void> {
  await new Promise<void>(resolve => {
    let settled = false;
    let disposable: { dispose: () => void } | null = null;
    let timeoutId: ReturnType<typeof window.setTimeout> | null = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      disposable?.dispose();
      resolve();
    };

    disposable = doc.slots.rootAdded.once(finish);
    timeoutId = window.setTimeout(finish, timeoutMs);
  });
}

export async function waitForLoadedDocRoot(
  doc: LoadedBlockSuiteDoc,
  timeoutMs: number
): Promise<boolean> {
  let keepWaitingForBlocks = true;
  while (keepWaitingForBlocks) {
    if (materializeLoadedDocRoot(doc)) {
      return true;
    }

    await waitForRootAddedOrTimeout(doc, timeoutMs);

    if (materializeLoadedDocRoot(doc)) {
      return true;
    }

    keepWaitingForBlocks = !hasLoadedBlocks(doc);
  }

  return false;
}
